import { Box, Divider, Grid, Typography, Button, Switch, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PageHeader from 'components/Header/Page';
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import PieInfoWidget from 'components/Widgets/PieInfoWidget';
import SegmentedBarChart from 'components/Widgets/SegmentedBarChart';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import RowTypography from 'components/Table/RowTypography';
import {
    fetchIncidentTechnologyList, fetchIncidentsSummary, fetchIncidentsBySectorMetrics, fetchP3IncidentsSummary, fetchHappySignalOverview, fetchSummaryMessageDetailsCosmos, fetchKBArticlePublicationTrend, fetchSummaryIncidentTrend,
    fetchAllIncidentsSummaryTrend,
    fetchHappySignalOverviewTrend,
    // --- We still need this for the capability mapping, even if we don't call it 4 times ---
    fetchIncidentTrend,
    fetchIncidentsByPrioritySummaryTrend,
    addOrUpdateInsights, deleteInsights,
    fetchTechnologyDataList, getInsights, fetchChannelMetrics, fetchResolutionMetrics, fetchL1L2L3Metrics
} from 'service/service';
import LoaderComponent from 'components/Loader';
import dayjs from 'dayjs';
import CustomBarChartWidget from 'components/Widgets/CustomBarChart';
import Insights from './Insights';
import { useTimezoneController } from 'Context/contexts/TimezoneContext';
import { useTheme } from "@mui/material/styles";
import PageWrapper from 'components/Common/PageWraper';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import MultiLineWidget from 'components/Widgets/MultiLineChart';
// --- FIX 2: Added missing import for ChartWidget ---
import ChartWidget from 'components/Widgets/ChartWidget';
import YearMonthPicker from 'components/Dropdown/YearMonthPicker';
import { SECTOR_OPTIONS } from 'utils/xopsUtil';
import MultiSelectAutocomplete from 'components/Inputs/MultiSelectAutocomplete';
import CaptureExportControls from 'utils/CaptureExportControls';

// --- START: DEFINITIONS FOR YOUR DATA ---
interface PieChartItem {
    name: string;
    value: number;
    formattedName: string;
}

interface Row {
    id: number;
    technology: string;
    totalIncidents: string;
    criticalIncidents: string;
    alerts: string;
}

interface IncidentPriorityData {
    YearMonth: string;
    veryLow?: number;
    low?: number;
    moderate?: number;
}

interface SlaData {
    YearMonth: string;
    avgSLA: number;
}

interface AllIncidentsData {
    YearMonth: string;
    userIncidents: number;
    automatedIncidents?: number;
    totalIncidents?: number;
}

interface ApiData {
    allIncidents?: AllIncidentsData[];
    mim?: any[];
    mimsYTD?: number;
    p3Incidents?: any[];
    incidentsByPriority?: IncidentPriorityData[];
    resolutionSLA?: SlaData[];
    responseSLA?: SlaData[];
}

type InsightsType = React.ComponentProps<typeof Insights>['insights'][number];
// --- END: DEFINITIONS FOR YOUR DATA ---


function ExecutiveSummary() {
    const theme = useTheme();

    // --- MODIFICATION: Reverted Sector to single string ---
    const [sectorName, setSectorName] = useState(() => {
        const storedValue = localStorage.getItem("xopsExecutiveSummarySector");
        return storedValue === null || storedValue === undefined ? ["all"] : JSON.parse(storedValue);
    });

    // --- MODIFICATION: Reverted Advance Filters to single string ---
    const [businessUnitName, setBusinessUnitName] = useState(['all']);
    const [departmentName, setDepartmentName] = useState(['all']);
    const [capabilityName, setCapabilityName] = useState(['all']);

    const [domainName, setDomainName] = useState(['all']);
    const [programName, setProgramName] = useState(['all']);

    const [, setYtdMimsCount] = useState<number | string>(0);
    const [assignmentGroupsCount, setAssignmentGroupsCount] = useState<number | string>(0);

    const isXOps = true;
    const [timezoneState] = useTimezoneController();

    // --- CHANGE  Standard filter function for most APIs ---
    // Returns "Business Unit", "Department"
    const getActiveFilter_Standard = () => {
        const filters: any = { type: undefined, area: undefined }
        if (!businessUnitName.includes('all')) {
            filters.businessUnit = businessUnitName
        } else if (!departmentName.includes('all')) {
            filters.department = departmentName
        } else if (!capabilityName.includes('all')) {
            filters.capability = capabilityName
        }
        return filters;
    };

    // --- CHANGE Special filter function for Summary Trend APIs ---
    // Returns "businessunit", "department" (lowercase)
    const getActiveFilter_Summary = () => {
        const filters: any = { type: undefined, area: undefined }
        if (!businessUnitName.includes('all')) {
            filters.businessUnit = businessUnitName
        } else if (!departmentName.includes('all')) {
            filters.department = departmentName
        } else if (!capabilityName.includes('all')) {
            filters.capability = capabilityName
        }
        return filters;
    };

    const timezoneFilter = timezoneState.timezone;
    const getInitialMonth = () => {
        return isXOps ? moment().subtract(1, 'month').format('YYYY-MM') : '0M';
    };
    const [customMonth, setCustomMonth] = useState(getInitialMonth());
    const [filterMonth, setFilterMonth] = useState(getInitialMonth());
    const [chartLoading, setChartLoading] = useState(true);
    const [p3ChartLoading, setP3ChartLoading] = useState(true);
    const [loader, setLoader] = useState<boolean>(false);
    const [sortingInfo] = useState({ sortBy: '', sortDir: '' });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [, setTotalPages] = useState(0);
    const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
    const [tableData, setTableData] = useState<Row[]>([]);
    const [barChartOptions, setBarChartOptions] = useState<any>({});
    const [, setWidgetData] = useState<any[]>([]);
    const [insights, setInsights] = useState<InsightsType[]>([]);
    const [availabilityData, setAvailabilityData] = useState<any[]>([]);
    const [avilabilityBarChartOptions, setAvilabilityBarChartOptions] = useState<any>({});
    const [avilabilityLoader, setAvilabilityLoader] = useState<boolean>(false);
    const [channelMetrics, setChannelMetrics] = useState<PieChartItem[]>([])
    const [resolutionMetrics, setResolutionMetrics] = useState<PieChartItem[]>([])
    const [sectorMetrics, setSectorMetrics] = useState<any>([])
    const [enableSectorCut, setEnableSectorCut] = useState<any>({})
    const [isClosed, setIsClosed] = useState(true);
    const [, setHappySignal] = useState<number>(0);
    const [isExporting, setIsExporting] = useState(false);
    const [, setHappySignalChange] = useState<string>('-');
    const [, setHappySignalScoreLoading] = useState(true);
    const [, setLostTime] = useState<string>('');
    const sectorOptionsList = SECTOR_OPTIONS;
    const [p3widgetData, setP3WidgetData] = useState<any[]>([]);
    const [allIncidentBarChartOptions, setAllIncidentBarChartOptions] = useState<any>({});
    const [allIncidentChartLoading, setAllIncidentChartLoading] = useState(true);

    const [knowledgeBaseOptions, setKnowledgeBaseOptions] = useState<any>({});
    const [daiAvailabilityData, setDaiAvailabilityData] = useState<any[]>([]);
    const [summaryMessageLoading, setSummaryMessageLoading] = useState(true);
    const [kbArticleLoading, setKbArticleLoading] = useState(true);

    const [l1l2l3MetricsData, setL1l2l3MetricsData] = useState<any>({ value: 0, change: 0, changeType: 'decrease', data: [] });

    const [happinessIndexTrendData, setHappinessIndexTrendData] = useState<{ [key: string]: number } | null>({});
    const [lastYearAvgData, setLastYearAvgData] = useState<[string, number][]>([]);
    const [happyTrendLoading, setHappyTrendLoading] = useState(true);

    const mimColorMapping = { 'MIMs': '#299BFF', 'Preventive MIMs': '#01C008', 'MIMs (YTD)': '#299BFF' };
    const p3IncidentsColorMapping = { 'Databricks': '#299BFF', 'PowerBI': '#FFC231', 'Teradata': '#00D08D', 'Tableau': '#FD783D', 'Presto': '#8884D8', 'Others': '#707070' };
    const intakeResolutionColorMapping = { 'Self service': '#299BFF', 'System generated': '#707070', 'Self healing': '#01C008' };
    const l1l2l3ColorMapping = { 'L1': '#00B8D9', 'L2': '#4CAF50', 'L3': '#9C27B0' };

    const [automatedIncidentsChartOptions, SetAutomatedIncidentsChartOptions] = useState<Record<string, any>>({});
    const [changeRequestsChartOptions, SetChangeRequestsChartOptions] = useState<Record<string, any>>({});
    const [serviceRequestsChartOptions, SetServiceRequestsChartOptions] = useState<Record<string, any>>({});
    const [problemTicketsChartOptions, SetProblemTicketsChartOptions] = useState<Record<string, any>>({});

    const [incidentsChartLoader, SetIncidentsChartLoader] = useState<boolean>(true);

    // --- MODIFICATION: Removed formatFilterForApi function ---

    const handleOpenClosedFilterChange = (event: any) => {
        const isClosed = event.target.checked;
        setIsClosed(isClosed);
    };

    const getMonthLabel = (yearMonth: string) => dayjs(`${yearMonth}-01`).format("MMM YYYY");

    const getAvailabilityColor = (value: number, threshold: number) =>
        value >= threshold ? theme.palette.success.main : theme.palette.error.main;

    const createDaiAvailabilityData = (data: any, hasValidData: boolean) => {
        if (!hasValidData) {
            return [
                { title: "YTD D&AI Availability", value: "N/A", valueColor: 'white', tooltipText: "Calculated Year to date, based on the outages of only the most critical Service Offerings." },
                { title: "YTD D&AI Platform Availability", value: "N/A", valueColor: 'white', tooltipText: "Calculated Year to date, based on the outages in Teradata, Data Integration BW Azure Tools, B2B Integration & Governance, Modern Cloud, Visualisation." },
                { title: "Assignment Groups", value: 0 },
                { title: "YTD MIMs (Actual)", value: 0 },
                { title: "YTD MIMs (Preventive)", value: 0 },
                { title: "Total Apps", value: 0 },
            ];
        }

        return [
            {
                title: "YTD D&AI Availability",
                value: `${data.TotalAvailability}%`,
                valueColor: getAvailabilityColor(data.TotalAvailability, 99.975),
                tooltipText: "Calculated Year to date, based on the outages of only the most critical Service Offerings."
            },
            {
                title: "YTD D&AI Platform Availability",
                value: `${data.TotalPlatformAvailability}%`,
                valueColor: getAvailabilityColor(data.TotalPlatformAvailability, 99.9),
                tooltipText: "Calculated Year to date, based on the outages in Teradata, Data Integration BW Azure Tools, B2B Integration & Governance, Modern Cloud, Visualisation."
            },
            { title: "Assignment Groups", value: data.TotalAssignmentGroups },
            { title: "YTD MIMs (Actual)", value: data.TotalMims - data.PreventiveMims },
            { title: "YTD MIMs (Preventive)", value: data.PreventiveMims },
            { title: "Total Apps", value: data.TotalApps },
        ];
    };

    // --- UPDATED: Added type and area arguments for Dynamic Filtering ---
    async function loadSummaryMessageData(dateFilter: string, isClosedDate: boolean, timezoneFilter: string, sector: string, type?: string, area?: string) {
        try {
            setSummaryMessageLoading(true);

            const requestPayload: any = { dateFilter, isClosedDate, timezoneFilter, sector };
            const hasFilter = type && area && area !== 'all';

            if (hasFilter) {
                requestPayload.type = type;
                requestPayload.area = area;
            }

            const res = await fetchSummaryMessageDetailsCosmos(requestPayload);
            const hasValidData = res.success && res.data?.summaryData?.length > 0;

            if (hasValidData) {
                const d = res.data.summaryData[0];
                const shouldUpdateCounts = !type && !area;

                if (shouldUpdateCounts) {
                    setYtdMimsCount(d.TotalMims);
                    setAssignmentGroupsCount(d.TotalAssignmentGroups);
                }

                setDaiAvailabilityData(createDaiAvailabilityData(d, true));
            } else {
                setDaiAvailabilityData(createDaiAvailabilityData(null, false));

                if (!type && !area) {
                    setYtdMimsCount(0);
                    setAssignmentGroupsCount(0);
                }
            }

        } catch (error) {
            console.error("Error fetching summary message details:", error);
            setDaiAvailabilityData(createDaiAvailabilityData(null, false));
        } finally {
            setSummaryMessageLoading(false);
        }
    }

    async function loadKBArticleTrendData(dateFilter: string, isClosedDate: boolean, timezoneFilter: string, sector: string) {
        try {
            setKbArticleLoading(true);
            // --- This chart is static, so 'type' and 'area' (metricsType) are hardcoded or removed ---
            const res = await fetchKBArticlePublicationTrend({ dateFilter, metricsType: 'Platform', isClosedDate, timezoneFilter, sector });
            if (res.success && res.data) {
                const labels = res.data.kbArticlePublicationTrend.map((item: any) =>
                    getMonthLabel(item.YearMonth)
                );
                const allMonths = res.data.kbArticlePublicationTrend.map((item: any) => item.YearMonth);

                const dataPoints = res.data.kbArticlePublicationTrend.map((item: any) => item.Count);

                const daiMap = new Map(
                    res.data.kbArticleDAIPublicationTrend.map((item: any) => [item.YearMonth, item.Count])
                );

                const dataPointsDAI = allMonths.map((month: any) => daiMap.get(month) ?? 0);

                const colors = ['#8884d8', '#FFBF00'];

                const maxVal = Math.max(...dataPoints);
                const minVal = Math.min(...dataPoints);
                const yMax = Math.ceil(maxVal / 10) * 10;
                const yMin = Math.max(0, Math.floor(minVal / 10) * 10);

                let maxValDAI = Math.max(...dataPointsDAI);
                let minValDAI = Math.min(...dataPointsDAI);

                if (!isFinite(maxValDAI) || maxValDAI === 0) {
                    maxValDAI = 10;
                    minValDAI = 0;
                }
                const yMaxDAI = Math.ceil(maxValDAI / 10) * 10;
                const yMinDAI = Math.max(0, Math.floor(minValDAI / 10) * 10);

                const intervalDAI = Math.max(1, Math.ceil(yMaxDAI / 5));

                const options = {
                    backgroundColor: 'transparent',
                    tooltip: { trigger: 'axis' },
                    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
                    xAxis: {
                        type: 'category',
                        boundaryGap: false,
                        data: labels,
                        axisLabel: { color: '#A7ADCB' }
                    },
                    yAxis: [{
                        type: 'value',
                        name: 'KB Articles',
                        min: yMin,
                        max: yMax,
                        position: 'left',
                        alignTicks: true,
                        splitLine: {
                            show: true,
                            lineStyle: {
                                type: 'dashed',
                                color: '#8884d8'
                            }
                        },
                        axisLine: {
                            show: true,
                            lineStyle: {
                                color: colors[0]
                            }
                        },
                        axisLabel: { color: '#A7ADCB' },
                    },
                    {
                        type: 'value',
                        min: yMinDAI,
                        max: yMaxDAI,
                        name: 'KB Articles D&AI',
                        position: 'right',
                        alignTicks: false,
                        interval: intervalDAI,
                        axisLabel: {
                            color: '#A7ADCB',
                            formatter: '{value}'
                        },
                        splitLine: {
                            show: true,
                            lineStyle: { type: 'dashed', color: '#004477' }
                        },
                        axisLine: {
                            show: true,
                            lineStyle: { color: colors[1] }
                        }
                    }
                    ],
                    series: [{
                        label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' },
                        name: 'KB Articles',
                        type: 'line',
                        smooth: true,
                        data: dataPoints,
                        yAxisIndex: 0,
                        areaStyle: {
                            color: {
                                type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                                colorStops: [
                                    { offset: 0, color: 'rgba(136, 132, 216, 0.5)' },
                                    { offset: 1, color: 'rgba(136, 132, 216, 0)' }
                                ]
                            }
                        },
                        itemStyle: { color: '#8884d8' }
                    },
                    {
                        label: { show: true, position: 'top', fontSize: 12, color: '#FFBF00' },
                        name: 'KB Articles D&AI',
                        type: 'line',
                        smooth: true,
                        data: dataPointsDAI,
                        yAxisIndex: 1,
                        areaStyle: {
                            color: {
                                type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                                colorStops: [
                                    { offset: 0, color: 'rgba(255, 191, 0, 0.4)' },
                                    { offset: 1, color: 'rgba(255, 191, 0, 0)' }
                                ]
                            }
                        },
                        itemStyle: { color: '#FFBF00' }
                    }]
                };
                setKnowledgeBaseOptions(options);
            }
        } catch (error) {
            console.error("Error fetching KB Article trend:", error);
        } finally {
            setKbArticleLoading(false);
        }
    }

    async function fetchHappyTrendChart(sector: string, filterMonth: any) {
        try {
            setHappyTrendLoading(true);
            const response = await fetchHappySignalOverviewTrend('all', 'all', sector, businessUnitName, departmentName, capabilityName, filterMonth);

            if (response.success && response.data) {
                const { happinessIndexTrend, happinessIndexLastYearAvg } = response.data;

                const trendData: { [key: string]: number } = {};
                happinessIndexTrend.forEach((item: any) => {
                    const formattedMonth = moment(item.month, 'MMM YYYY').format('YYYY-MM');
                    trendData[formattedMonth] = item.value;
                });

                setHappinessIndexTrendData(trendData);

                const avgPreviousYearTrendData: [string, number][] = Object.keys(trendData).map(
                    (month) => [month, happinessIndexLastYearAvg] as [string, number]
                );

                setLastYearAvgData(avgPreviousYearTrendData);
            } else {
                setHappinessIndexTrendData({});
                setLastYearAvgData([]);
            }
        } catch (error) {
            console.error('Error while fetching happy signal trend chart data:', error);
            setHappinessIndexTrendData({});
            setLastYearAvgData([]);
        } finally {
            setHappyTrendLoading(false);
        }
    }

    async function fetchChartData(selectedDate: any, isClosedDate: boolean, timezoneFilter: string, sector: string,
        type?: string, area?: string, domain?: string[], businessUnit?: string[], department?: string[], capability?: string[]) {
        try {
            setChartLoading(true);
            const res = await fetchIncidentsSummary(selectedDate, isClosedDate, timezoneFilter, sector, type, area, domain, businessUnit, department, capability);
            if (res.success) {
                const result = transformData(res.data);
                setWidgetData(result);

                if (type && area) {
                    setYtdMimsCount(res.data.mimsYTD ?? 0);
                    setAssignmentGroupsCount(res.data.agCount ?? 0);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setChartLoading(false);
        }
    }

    async function fetchIncidentsByPriorityChartData(params: {
        selectedDate: any;
        isClosedDate: boolean;
        timezoneFilter: string;
        sector: string;
        type?: string;
        area?: string;
        domain?: string[];
        businessUnit?: string[];
        department?: string[];
        capability?: string[];
    }) {
        try {
            setChartLoading(true);
            const res = await fetchIncidentsByPrioritySummaryTrend(params);
            if (res.success) {
                transformData(res.data);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setChartLoading(false);
        }
    }

    // --- CHANGE : This chart is now DYNAMIC ---
    async function fetchAllIncidentChartData(selectedDate: any, isClosedDate: boolean, timezoneFilter: string, sector: string,
        type?: string, area?: string) {
        try {
            setAllIncidentChartLoading(true);
            // --- This API now accepts 'type' and 'area' ---
            const res = await fetchAllIncidentsSummaryTrend(selectedDate, isClosedDate, timezoneFilter, sector, type, area);
            if (res.success) {
            }
        } catch (error) {
            console.error(error);
        } finally {
            setAllIncidentChartLoading(false);
        }
    }

    async function fetchP3IncidentChartData(selectedDate: any, isClosedDate: boolean, timezoneFilter: string,
        type?: string, area?: string) {
        try {
            setP3ChartLoading(true);
            // --- This chart remains static, so 'type' and 'area' are removed ---
            const res = await fetchP3IncidentsSummary(selectedDate, isClosedDate, timezoneFilter, undefined, undefined);
            if (res.success) {
                const result = transformP3IncidentData(res.data);
                setP3WidgetData(result);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setP3ChartLoading(false);
        }
    }

    const availabilityPlatforms = ['B2B Integration & Governance', 'Data Integration BW Azure Tools', 'Modern Cloud', 'Teradata', 'Visualization']

    async function availabilityChartData(selectedMonth: any, metricsType: string, isClosedDate: boolean, timezoneFilter: string,
        sector: string, type?: string, area?: string) {
        try {
            setAvilabilityLoader(true);
            // --- This chart remains static, 'type' and 'area' (BU, Dept, Cap) are ignored ---
            const businessUnit = undefined;
            const department = undefined;
            const capability = undefined;

            const res = await fetchTechnologyDataList({ selectedMonth, metricsType, isClosedDate, timezoneFilter, sector, businessUnit, department, capability });
            if (res.success && res.data.data) {
                const formattedAvailability = res.data.data.filter((item: any) => availabilityPlatforms.includes(item.platform)).sort().map((item: any) => ({ name: item.platform, value: item.availability }));
                setAvailabilityData(formattedAvailability);
                const xAxisLabels = formattedAvailability.map((item: any) => item.name);
                const seriesData = formattedAvailability.map((item: any) => item.value);
                const lowestAvailabilityValue = formattedAvailability.length > 0 ? Math.min(...formattedAvailability.map((i: { value: number }) => i.value)) : 100;
                const minYValue = lowestAvailabilityValue > 99.90 ? 99.90 : Math.floor(lowestAvailabilityValue);
                const BarChartoptions = {
                    backgroundColor: 'transparent',
                    tooltip: { trigger: 'axis', formatter: (params: any) => `${params[0].name}<br/>${params[0].seriesName} :  ${params[0].value}%` },
                    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
                    xAxis: { type: 'category', data: xAxisLabels, axisTick: { show: false }, axisLine: { show: true }, axisLabel: { color: '#A7ADCB', interval: 0, rotate: 30 } },
                    yAxis: { type: 'value', min: minYValue, max: 100, splitLine: { show: false }, axisLabel: { color: '#A7ADCB', formatter: (val: number) => val.toFixed(1) } },
                    series: [{
                        label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' },
                        name: 'Availability', type: 'bar', data: seriesData, itemStyle: { color: '#299BFF' },
                        markLine: {
                            symbol: 'none',
                            label: { show: true, position: 'insideStartBottom', formatter: '99.975% SLA', color: '#FF6B55', fontWeight: 'bold', },
                            lineStyle: { color: '#FF6B55', width: 2, type: 'solid', },
                            data: [{ yAxis: 99.975, name: 'SLA' }],
                        },
                    }],
                };
                setAvilabilityBarChartOptions(BarChartoptions);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setAvilabilityLoader(false);
        }
    }

    async function loadChannelMetrics(selectedDate: any, isClosedDate: boolean, timezoneFilter: string, sector: string,
        type?: string, area?: string) {
        try {
            setChartLoading(true);
            const res = await fetchChannelMetrics(selectedDate, isClosedDate, timezoneFilter, sector, type, area);
            if (res.success && res.data && res.data.channelMetric.length > 0) {
                const item = res.data.channelMetric[0];
                const totalCount = item.selfServeCount + item.humanInLoopCount + item.systemGeneratedCount;
                if (totalCount > 0) {
                    const channelData = [
                        { name: 'Self service', value: item.selfServeCount, formattedName: `${Math.round(item.selfServeCount / totalCount * 100)}%` },
                        { name: 'Human in loop', value: item.humanInLoopCount, formattedName: `${Math.round(item.humanInLoopCount / totalCount * 100)}%` },
                        { name: 'System generated', value: item.systemGeneratedCount, formattedName: `${Math.round(item.systemGeneratedCount / totalCount * 100)}%` }
                    ];
                    setChannelMetrics(channelData);
                } else {
                    setChannelMetrics([{ name: 'Self service', value: 0, formattedName: `0%` }, { name: 'Human in loop', value: 0, formattedName: `0%` }, { name: 'System generated', value: 0, formattedName: `0%` }]);
                }
            } else {
                setChannelMetrics([{ name: 'Self service', value: 0, formattedName: `0%` }, { name: 'Human in loop', value: 0, formattedName: `0%` }, { name: 'System generated', value: 0, formattedName: `0%` }]);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setChartLoading(false);
        }
    }

    async function loadResolutionMetrics(selectedDate: any, isClosedDate: boolean, timezoneFilter: string, sector: string,
        type?: string, area?: string) {
        try {
            setChartLoading(true);
            const res = await fetchResolutionMetrics(selectedDate, isClosedDate, timezoneFilter, sector, type, area);
            if (res.success && res.data && res.data.resolutionMetric.length > 0) {
                const item = res.data.resolutionMetric[0];
                const totalCount = item.selfHealingCount + item.humanInLoopCount;
                if (totalCount > 0) {
                    const resolutionData = [
                        { name: 'Self healing', value: item.selfHealingCount, formattedName: `${Math.round(item.selfHealingCount / totalCount * 100)}%` },
                        { name: 'Human in loop', value: item.humanInLoopCount, formattedName: `${Math.round(item.humanInLoopCount / totalCount * 100)}%` }
                    ];
                    setResolutionMetrics(resolutionData);
                } else {
                    setResolutionMetrics([{ name: 'Self Healing', value: 0, formattedName: `0%` }, { name: 'Human in loop', value: 0, formattedName: `0%` }]);
                }
            } else {
                setResolutionMetrics([{ name: 'Self Healing', value: 0, formattedName: `0%` }, { name: 'Human in loop', value: 0, formattedName: `0%` }]);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setChartLoading(false);
        }
    }

    async function loadL1L2L3Metrics(selectedDate: any, isClosedDate: boolean, timezoneFilter: string, sector: string,
        type?: string, area?: string) {
        try {
            setChartLoading(true);
            const res = await fetchL1L2L3Metrics(selectedDate, isClosedDate, timezoneFilter, sector, type, area);
            if (res.success && res.data?.resLevelMetric?.length > 0) {
                const item = res.data.resLevelMetric[0];
                const totalValue = item.L1Count + item.L2Count + item.L3Count;

                const change = item.lastMonthCount === 0
                    ? (item.currentMonthCount > 0 ? 100 : 0)
                    : ((item.currentMonthCount - item.lastMonthCount) / item.lastMonthCount) * 100;

                const seriesData = [
                    { name: 'L1', value: item.L1Count, formattedName: `${item.L1Count}` },
                    { name: 'L2', value: item.L2Count, formattedName: `${item.L2Count}` },
                    { name: 'L3', value: item.L3Count, formattedName: `${item.L3Count}` }
                ];

                setL1l2l3MetricsData({
                    value: totalValue,
                    change: Math.abs(Number(change.toFixed(2))),
                    changeType: item.currentMonthCount > item.lastMonthCount ? 'increase' : 'decrease',
                    data: seriesData
                });
            } else {
                setL1l2l3MetricsData({
                    value: 0, change: 0, changeType: 'decrease',
                    data: [{ name: 'L1', value: 0, formattedName: '0' }, { name: 'L2', value: 0, formattedName: '0' }, { name: 'L3', value: 0, formattedName: '0' }]
                });
            }
        } catch (error) {
            console.error("Error fetching L1/L2/L3 metrics:", error);
            setL1l2l3MetricsData({
                value: 0, change: 0, changeType: 'decrease',
                data: [{ name: 'L1', value: 0, formattedName: '0' }, { name: 'L2', value: 0, formattedName: '0' }, { name: 'L3', value: 0, formattedName: '0' }]
            });
        } finally {
            setChartLoading(false);
        }
    }

    const loadIncidentSectorMetrics = async (selectedDate: any, isClosedDate: boolean,
        type?: string, area?: string) => {
        try {
            setChartLoading(true);
            const res = await fetchIncidentsBySectorMetrics(selectedDate, isClosedDate, type, area);
            if (res.success && res.data) {
                const newSectorMetrics = {
                    mimSectorData: [],
                    problemBacklogSectorData: [],
                    p3SectorData: res.data.p3IncidentsSector?.[0]?.sector.map((sector: any) => ({ name: sector.name, value: sector.count, formattedName: `${sector.count}` })) || []
                };
                setSectorMetrics(newSectorMetrics);
            } else {
                setSectorMetrics({ mimSectorData: [], problemBacklogSectorData: [], p3SectorData: [] });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setChartLoading(false);
        }
    }

    async function loadHappySignalsData(selectedDate: any, sector: string) {
        try {
            setHappySignalScoreLoading(true);
            const res = await fetchHappySignalOverview(selectedDate, 'all', 'all', sector);
            if (res.success && res.data) {
                if (res.data.happinessIndex) {
                    setHappySignal(res.data.happinessIndex.value);
                    setHappySignalChange(res.data.happinessIndex.change);
                }
                if (res.data.averageLostTime) {
                    setLostTime(res.data.averageLostTime.value);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setHappySignalScoreLoading(false);
        }
    }

    useEffect(() => {
        const currentMonth = getInitialMonth();
        setFilterMonth(currentMonth);
    }, []);

    // --- CHANGE : Using both standard and summary filter types ---
    useEffect(() => {
        const { type: standardType, area: standardArea } = getActiveFilter_Standard();
        const { type: summaryType, area: summaryArea } = getActiveFilter_Summary();

        // --- APIs using STANDARD filter type ("Business Unit") ---
        fetchChartData(filterMonth, isClosed, timezoneFilter, sectorName, standardType, standardArea, domainName);
        listTableData({ pageNumber: 1, size: 100, sortingInfo, selectedDate: filterMonth, isClosedDate: isClosed, timezoneFilter, sector: sectorName, type: standardType, area: standardArea });
        loadChannelMetrics(filterMonth, isClosed, timezoneFilter, sectorName, standardType, standardArea);
        loadResolutionMetrics(filterMonth, isClosed, timezoneFilter, sectorName, standardType, standardArea);
        loadIncidentSectorMetrics(filterMonth, isClosed, standardType, standardArea);
        loadL1L2L3Metrics(filterMonth, isClosed, timezoneFilter, sectorName, standardType, standardArea);
        fetchIncidentsByPriorityChartData({
            selectedDate: filterMonth,
            isClosedDate: isClosed,
            timezoneFilter,
            sector: sectorName,
            type: standardType,
            area: standardArea,
            domain: domainName
        });

        // --- APIs using SUMMARY filter type ("businessunit") ---
        fetchIncidentsChartData(isClosed, timezoneFilter, sectorName, summaryType, summaryArea, filterMonth);
        fetchAllIncidentChartData(filterMonth, isClosed, timezoneFilter, sectorName, summaryType, summaryArea);

        // --- STATIC APIs (No filter type) ---
        fetchP3IncidentChartData(filterMonth, isClosed, timezoneFilter);
        availabilityChartData(filterMonth, 'Platform', isClosed, timezoneFilter, sectorName);
        loadKBArticleTrendData(filterMonth, isClosed, timezoneFilter, sectorName);

        // --- Other APIs ---
        fetchInsights(filterMonth);
        loadHappySignalsData(filterMonth, sectorName);
        loadSummaryMessageData(filterMonth, isClosed, timezoneFilter, sectorName);
        fetchHappyTrendChart(sectorName, filterMonth);

    }, [timezoneFilter, filterMonth, isClosed, sectorName, businessUnitName, departmentName, capabilityName, domainName]);

    async function listTableData({ pageNumber, size, sortingInfo, selectedDate, isClosedDate, timezoneFilter, sector, type, area }: any) {
        try {
            setLoader(true);
            const res = await fetchIncidentTechnologyList({
                page: pageNumber, size, sortBy: sortingInfo.sortBy, orderBy: sortingInfo.sortDir,
                dateFilter: selectedDate, isClosedDate: isClosedDate, timezoneFilter: timezoneFilter,
                sector: sector,
                type: type,
                area: area
            });
            if (res.success) {
                const result = calculateTableData(res.data.result);
                setTableData(result);
                setPaginationData(res.data.page);
                setPage(res.data.page.currentPage);
                setPageSize(res.data.page.size);
                setTotalPages(res.data.page.totalPages);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoader(false);
        }
    }

    const transformData = (response: any) => {
        const data: ApiData = response || {};
        let widgetData: any[] = [];

        if (data.mim && data.mim.length > 0) {
            const item = data.mim[0];
            const change = item.lastMonthCount === 0
                ? (item.currentMonthCount > 0 ? 100 : 0)
                : ((item.currentMonthCount - item.lastMonthCount) / item.lastMonthCount) * 100;

            const chartData = [
                { name: 'MIMs', value: item.mimsCount, formattedName: `${item.mimsCount}` },
                { name: 'Preventive MIMs', value: item.preventiveCount, formattedName: `${item.preventiveCount}` }
            ];

            widgetData.push({
                title: 'MIM',
                value: item.totalCount,
                data: chartData,
                change: Math.abs(Number(change.toFixed(2))),
                changeType: item.currentMonthCount > item.lastMonthCount ? 'increase' : 'decrease'
            });
        }
        // --- UPDATED: Handle new Mims_Count_YTD logic for business metrics ---
        else if (data.mimsYTD !== undefined && data.mimsYTD !== null) {
            const val = data.mimsYTD;
            widgetData.push({
                title: 'MIM',
                value: val,
                data: [{ name: 'MIMs (YTD)', value: val, formattedName: `${val}` }],
                change: 0,
                changeType: ''
            });
        }

        if (data.incidentsByPriority) {
            const allMonths = new Set<string>([
                ...(data.incidentsByPriority?.map((d) => d.YearMonth) || []),
                ...(data.resolutionSLA?.map((d) => d.YearMonth) || []),
                ...(data.responseSLA?.map((d) => d.YearMonth) || [])
            ]);
            const sortedMonths = Array.from(allMonths).sort();

            const incidentsMap = new Map((data.incidentsByPriority ?? []).map(item => [item.YearMonth, item]));

            const xAxisLabels = sortedMonths.map(month => getMonthLabel(month));
            const veryLowData = sortedMonths.map(month => incidentsMap.get(month)?.veryLow || 0);
            const lowData = sortedMonths.map(month => incidentsMap.get(month)?.low || 0);
            const moderateData = sortedMonths.map(month => incidentsMap.get(month)?.moderate || 0);

            const allBarValues = [...veryLowData, ...lowData, ...moderateData];
            const maxValue = Math.max(0, ...allBarValues);
            const stepSize = Math.max(100, Math.ceil(maxValue / 4 / 100) * 100);
            const maxYAxis = Math.ceil(maxValue / stepSize) * stepSize;

            const options = {
                textStyle: { color: '#A7ADCB' }, backgroundColor: '#003771', tooltip: { trigger: 'axis' },
                legend: { data: ['Very Low', 'Low', 'Moderate', 'Response SLA %', 'Resolution SLA %'], bottom: 0, textStyle: { color: '#A7ADCB' }, type: 'scroll', pageIconColor: '#A7ADCB', pageIconInactiveColor: '#444C63', pageTextStyle: { color: '#A7ADCB', fontSize: 10 }, pageButtonItemGap: 5, pageButtonGap: 10, },
                grid: { left: '5%', right: '5%', bottom: '10%', containLabel: true },
                xAxis: [{ type: 'category', data: xAxisLabels, axisTick: { show: false }, axisLine: { show: true }, axisLabel: { color: '#A7ADCB' } }],
                yAxis: [
                    { type: 'value', min: 0, max: maxYAxis > 0 ? maxYAxis : 100, interval: stepSize, axisLabel: { formatter: '{value}', color: '#A7ADCB' }, splitLine: { show: false } },
                    { type: 'value', min: 0, max: 100, interval: 25, axisLabel: { formatter: '{value}%', color: '#A7ADCB' }, splitLine: { show: false } }
                ],
                series: [
                    { label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' }, name: 'Very Low', type: 'bar', data: veryLowData, itemStyle: { color: '#0D8EFF' }, barGap: '10%' },
                    { label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' }, name: 'Low', type: 'bar', data: lowData, itemStyle: { color: '#00D08D' }, barGap: '10%' },
                    { label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' }, name: 'Moderate', type: 'bar', data: moderateData, itemStyle: { color: '#FFC231' }, barGap: '10%' }
                ]
            };
            setBarChartOptions(options);
        }

        if (data.allIncidents || data.resolutionSLA || data.responseSLA) {
            const allMonths = new Set<string>([
                ...(data.allIncidents?.map((d) => d.YearMonth) || []),
                ...(data.resolutionSLA?.map((d) => d.YearMonth) || []),
                ...(data.responseSLA?.map((d) => d.YearMonth) || [])
            ]);
            const sortedMonths = Array.from(allMonths).sort();

            const incidentsMap = new Map((data.allIncidents ?? []).map(item => [item.YearMonth, item]));
            const resolutionSlaMap = new Map((data.resolutionSLA ?? []).map((item) => [item.YearMonth, item.avgSLA]));
            const responseSlaMap = new Map((data.responseSLA ?? []).map((item) => [item.YearMonth, item.avgSLA]));

            const xAxisLabels = sortedMonths.map(month => getMonthLabel(month));
            const userIncidentsData = sortedMonths.map(month => incidentsMap.get(month)?.userIncidents || 0);
            const automatedIncidentsData = sortedMonths.map(month => incidentsMap.get(month)?.automatedIncidents || 0);
            const totalIncidentsData = sortedMonths.map(month => incidentsMap.get(month)?.totalIncidents || 0);
            const resolutionSlaData = sortedMonths.map(month => resolutionSlaMap.get(month) ?? null);
            const responseSlaData = sortedMonths.map(month => responseSlaMap.get(month) ?? null);

            const allBarValues = [...userIncidentsData, ...automatedIncidentsData, ...totalIncidentsData];
            const maxValue = Math.max(0, ...allBarValues);
            const stepSize = Math.max(100, Math.ceil(maxValue / 4 / 100) * 100);
            const maxYAxis = Math.ceil(maxValue / stepSize) * stepSize;

            const options = {
                textStyle: { color: '#A7ADCB' }, backgroundColor: '#003771', tooltip: { trigger: 'axis' },
                legend: { data: ['User Incidents', 'Automated Incidents', 'Total Incidents', 'Response SLA %', 'Resolution SLA %'], bottom: 0, textStyle: { color: '#A7ADCB' }, type: 'scroll', pageIconColor: '#A7ADCB', pageIconInactiveColor: '#444C63', pageTextStyle: { color: '#A7ADCB', fontSize: 10 }, pageButtonItemGap: 5, pageButtonGap: 10, },
                grid: { left: '5%', right: '5%', bottom: '10%', containLabel: true },
                xAxis: [{ type: 'category', data: xAxisLabels, axisTick: { show: false }, axisLine: { show: true }, axisLabel: { color: '#A7ADCB' } }],
                yAxis: [
                    { type: 'value', min: 0, max: maxYAxis > 0 ? maxYAxis : 100, interval: stepSize, axisLabel: { formatter: '{value}', color: '#A7ADCB' }, splitLine: { show: false } },
                    { type: 'value', min: 0, max: 100, interval: 25, axisLabel: { formatter: '{value}%', color: '#A7ADCB' }, splitLine: { show: false } }
                ],
                series: [
                    { label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' }, name: 'User Incidents', type: 'bar', data: userIncidentsData, itemStyle: { color: '#0D8EFF' }, barGap: '10%' },
                    { label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' }, name: 'Automated Incidents', type: 'bar', data: automatedIncidentsData, itemStyle: { color: '#00D08D' }, barGap: '10%' },
                    { label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' }, name: 'Total Incidents', type: 'bar', data: totalIncidentsData, itemStyle: { color: '#FFC231' }, barGap: '10%' },
                    { label: { show: true, position: 'top', fontSize: 10, color: '#F0F0F0' }, name: 'Response SLA %', type: 'line', yAxisIndex: 1, smooth: true, data: responseSlaData, itemStyle: { color: '#F5359E' } },
                    { label: { show: true, position: 'bottom', fontSize: 10, color: '#F0F0F0' }, name: 'Resolution SLA %', type: 'line', yAxisIndex: 1, smooth: true, data: resolutionSlaData, itemStyle: { color: '#FF6B55' } }
                ]
            };
            setAllIncidentBarChartOptions(options);
        }

        return widgetData;
    };

    const transformP3IncidentData = (response: any) => {
        const data = response?.data || response || {};
        let widgetData: any[] = [];

        if (data.p3Incidents?.length > 0) {
            const item = data.p3Incidents[0];
            const change = item.lastMonthCount === 0
                ? (item.currentMonthCount > 0 ? 100 : 0)
                : ((item.currentMonthCount - item.lastMonthCount) / item.lastMonthCount) * 100;
            const chartData = (item.technology || []).map((tech: any) => ({
                name: tech.name,
                value: tech.count,
                formattedName: `${tech.count}`
            }));

            widgetData.push({
                title: 'P3 Incidents',
                value: item.totalCount,
                data: chartData,
                change: Math.abs(Number(change.toFixed(2))),
                changeType: item.currentMonthCount > item.lastMonthCount ? 'increase' : 'decrease'
            });
        }
        return widgetData;
    };

    const calculateTableData = (rawData: any[]) => {
        const totalRow = rawData.reduce((acc, row) => { acc.totalIncidents += row.totalIncidents; acc.criticalIncidents += row.criticalIncidents; acc.alerts += row.alerts; return acc; }, { totalIncidents: 0, criticalIncidents: 0, alerts: 0 });
        const finalData = [...rawData, { id: 'total-row', technology: '', totalIncidents: totalRow.totalIncidents.toLocaleString(), criticalIncidents: totalRow.criticalIncidents.toLocaleString(), alerts: totalRow.alerts.toLocaleString() }];
        return finalData;
    };

    const defaultColumnNames = ['Assignment Group', 'Total Incidents', 'Critical Incidents (P3)', 'Alerts'];
    let dynamicWidth: any;
    useEffect(() => {
        const handleResize = () => { dynamicWidth = window.innerWidth < 600 ? 300 : (window.innerWidth / 4) - 100; };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const getColumns = () => {
        const columnMap: Array<{ key: string; label: string }> = [{ key: 'technology', label: 'Assignment Group' }, { key: 'totalIncidents', label: 'Total Incidents' }, { key: 'criticalIncidents', label: 'Critical Incidents (P3)' }, { key: 'alerts', label: 'Alerts' }];
        return columnMap.map((col) => {
            if (col.key === 'technology') {
                return { key: col.key, name: col.label, resizable: true, width: dynamicWidth, formatter: ({ row }: any) => row.id === 'total-row' ? <RowTypography style={{ fontWeight: 'bold', width: '100%', color: '#fff' }}>TOTAL</RowTypography> : row.technology };
            } else {
                return { key: col.key, name: col.label, resizable: true, width: dynamicWidth, formatter: ({ row }: any) => row.id === 'total-row' ? <RowTypography style={{ fontWeight: 'bold', width: '100%' }}>{row[col.key]}</RowTypography> : row[col.key] };
            }
        });
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        // --- CHANGE : Use standard filter getter ---
        const { type, area } = getActiveFilter_Standard();
        listTableData({
            pageNumber: value, size: pageSize, sortingInfo, selectedDate: filterMonth, isClosedDate: isClosed, timezoneFilter: timezoneFilter,
            sector: sectorName, type, area
        });
    };
    const handlepageSizeChange = (event: any) => {
        // --- CHANGE  Use standard filter getter ---
        const { type, area } = getActiveFilter_Standard();
        listTableData({
            pageNumber: 1, size: event.target.value, sortingInfo, selectedDate: filterMonth, isClosedDate: isClosed, timezoneFilter: timezoneFilter,
            sector: sectorName, type, area
        });
    };
    const handleSorting = (sortingInfo: any) => {
        // --- CHANGE : Use standard filter getter ---
        const { type, area } = getActiveFilter_Standard();
        listTableData({
            pageNumber: page, size: pageSize, sortingInfo, selectedDate: filterMonth, isClosedDate: isClosed, timezoneFilter: timezoneFilter,
            sector: sectorName, type, area
        });
    };

    const handleDateChange = (selectedDate: string) => {
        setFilterMonth(selectedDate);
    };

    // --- THIS IS THE FIX ---
    const fetchInsights = async (selectedMonth: string) => {
        setLoader(true);
        try {
            const response = await getInsights('Incidents Summary', '', selectedMonth);
            if (response.success) { setInsights(response.data.data); }
        } catch (error) {
            console.error('Failed to get insights:', error);
        } finally {
            setLoader(false);
        }
    };
    const handleAddInsight = async (newInsight: any) => {
        try {
            setLoader(true);
            const response = await addOrUpdateInsights({ title: newInsight?.title, xopsType: 'Incidents Summary', description: newInsight?.description, month: filterMonth });
            if (response.success) {
                fetchInsights(filterMonth);
            }
        } catch (error) {
            console.error('Failed to add insight:', error);
        } finally {
            setLoader(false);
        }
    };
    const handleUpdateInsight = async (updatedInsight: any) => {
        try {
            setLoader(true);
            const response = await addOrUpdateInsights({ title: updatedInsight?.title, xopsType: 'Incidents Summary', description: updatedInsight?.description, insight_id: updatedInsight?.id, month: filterMonth });
            if (response.success) {
                fetchInsights(filterMonth);
            }
        } catch (error) {
            console.error('Failed to update insight:', error);
        } finally {
            setLoader(false);
        }
    };
    const handleDeleteInsight = async (insightId: number) => {
        try {
            setLoader(true);
            const response = await deleteInsights(insightId);
            if (response.success) {
                fetchInsights(filterMonth);
            }
        } catch (error) {
            console.error('Failed to delete insight:', error);
        } finally {
            setLoader(false);
        }
    };

    const incidentIntakeTooltip = [{ title: 'System Generated', description: 'Automated incidents created by ServiceNow Alerts.', }, { title: 'Self-Service', description: 'Incidents created through Chester, ServiceNow Self-Service portal & Webchat', }, { title: 'Human in the loop', description: 'Incidents created through human coordination via emails, Teams IM, Direct walk-in & Phone call.', },]
    const incidentResolutionTooltip = [{ title: 'Self-Healing (L0)', description: 'Incidents resolved without human intervention through BOTs & Auto correction identified by "Resolved By" & "Closed By" fields in ServiceNow.', }, { title: 'Human in the Loop (L1, L2 & L3)', description: 'Incidents Resolved by sustain personnel.', },]

    const handleMultiSelection = (selectedOptions: { label: string; value: string }[], allOptions: { label: string; value: string }[], currentValues: string[], reason?: string) => {
        let newValues = selectedOptions.map(option => option.value);
        if (currentValues.every(v => newValues.includes(v)) && newValues.every(v => currentValues.includes(v))) {
            return currentValues
        }
        const optionsWithAll = allOptions.map(option => option.value)
        const optionsWithoutAll = optionsWithAll.filter(v => v !== 'all')

        if (newValues?.length === optionsWithoutAll.length && optionsWithoutAll.every(v => newValues.includes(v))) {
            newValues.push('all')
            return newValues
        } else if (newValues?.length === optionsWithAll.length && optionsWithAll.every(v => newValues.includes(v))) {
            return newValues
        } else {
            newValues = newValues.filter(v => v !== 'all')
        }

        return newValues
    }
    const handleBusinessUnitChange = (selectedOptions: { label: string; value: string }[], reason?: string) => {
        setBusinessUnitName(handleMultiSelection(selectedOptions, businessUnitOptions, businessUnitName, reason));
    };

    const handleDepartmentChange = (selectedOptions: { label: string; value: string }[], reason?: string) => {
        setDepartmentName(handleMultiSelection(selectedOptions, departmentOptions, departmentName, reason));
    };

    const handleCapabilityChange = (selectedOptions: { label: string; value: string }[], reason?: string) => {
        setCapabilityName(handleMultiSelection(selectedOptions, capabilityOptions, capabilityName, reason));
    };

    // --- MODIFICATION: New single-select handler for Sector ---
    const handleSectorChange = (selectedOptions: { label: string; value: string }[], reason?: string) => {
        const updatedSectorFilter = handleMultiSelection(selectedOptions, sectorOptionsList, sectorName, reason)
        setSectorName(updatedSectorFilter);
        localStorage.setItem('xopsExecutiveSummarySector', JSON.stringify(updatedSectorFilter));
    };

    const handleDomainChange = (selectedOptions: { label: string; value: string }[], reason?: string) => {
        setDomainName(handleMultiSelection(selectedOptions, domainOptions, domainName, reason));
    };

    const handleProgramChange = (selectedOptions: { label: string; value: string }[], reason?: string) => {
        setProgramName(handleMultiSelection(selectedOptions, programOptions, programName, reason));
    };

    const handleClearAllFilters = () => {
        setDomainName(['all']);
        setProgramName(['all']);
        setBusinessUnitName(['all']);
        setDepartmentName(['all']);
        setCapabilityName(['all']);
        setSectorName('all');
        localStorage.setItem('xopsExecutiveSummarySector', 'all');
    };

    // --- MODIFICATION: Removed multi-select handler for Sector ---


    // ---
    // --- CHANGE: Reverted this function to its simple, original state ---
    // --- It no longer calls fetchIncidentTrend 4 times. ---
    // ---
    const fetchIncidentsChartData = async (isClosedDate: boolean, timezoneFilter: string, sector: string,
        type?: string, area?: string, filterMonth?: string) => {
        try {
            SetIncidentsChartLoader(true);

            if (type && area) {
                const [automatedIncRes, crRes, srRes, prRes] = await Promise.all([
                    fetchIncidentTrend({ metricsType: type, technology: area, chartType: 'Automated Incidents', sector }),
                    fetchIncidentTrend({ metricsType: type, technology: area, chartType: 'Change Requests', sector }),
                    fetchIncidentTrend({ metricsType: type, technology: area, chartType: 'Service Requests', sector }),
                    fetchIncidentTrend({ metricsType: type, technology: area, chartType: 'Problem Tickets', sector }),
                ])
                if (automatedIncRes?.data) {
                    const automatedOptions = buildIncidentChartOption(automatedIncRes.data.currentYearIncident, automatedIncRes.data.lastYearIncident, 'Automated Incidents');
                    SetAutomatedIncidentsChartOptions(automatedOptions);
                }
                if (crRes?.data) {
                    const changeRequestsOptions = buildIncidentChartOption(crRes.data.currentYearIncident, crRes.data.lastYearIncident, 'Change Requests');
                    SetChangeRequestsChartOptions(changeRequestsOptions);
                }
                if (srRes?.data) {
                    const serviceRequestsOptions = buildIncidentChartOption(srRes.data.currentYearIncident, srRes.data.lastYearIncident, 'Service Requests');
                    SetServiceRequestsChartOptions(serviceRequestsOptions);
                }
                if (prRes?.data) {
                    const problemTicketsOptions = buildIncidentChartOption(prRes.data.currentYearIncident, prRes.data.lastYearIncident, 'Problem Tickets');
                    SetProblemTicketsChartOptions(problemTicketsOptions);
                }
            } else {
                // --- This is now the ONLY call made by this function ---
                const res = await fetchSummaryIncidentTrend({
                    filterMonth,
                    isClosedDate,
                    timezoneFilter,
                    sector,
                    type, // This will be the (lowercase) summaryType
                    area  // This will be the summaryArea
                });

                if (res.success) {
                    const automatedOptions = buildIncidentChartOption(res.data.automatedIncidentsCurrentYearIncident, res.data.automatedIncidentsLastYearIncident, 'Automated Incidents');
                    SetAutomatedIncidentsChartOptions(automatedOptions);
                    const changeRequestsOptions = buildIncidentChartOption(res.data.changeRequestsCurrentYearIncident, res.data.changeRequestsLastYearIncident, 'Change Requests');
                    SetChangeRequestsChartOptions(changeRequestsOptions);
                    const serviceRequestsOptions = buildIncidentChartOption(res.data.serviceRequestsCurrentYearIncident, res.data.serviceRequestsLastYearIncident, 'Service Requests');
                    SetServiceRequestsChartOptions(serviceRequestsOptions);
                    const problemTicketsOptions = buildIncidentChartOption(res.data.problemTicketsCurrentYearIncident, res.data.problemTicketsLastYearIncident, 'Problem Tickets');
                    SetProblemTicketsChartOptions(problemTicketsOptions);
                }
            }
        } catch (error) {
            console.error('Error fetching incident trend data:', error);
            // Set empty options on error
            SetAutomatedIncidentsChartOptions(buildIncidentChartOption([], [], 'Automated Incidents'));
            SetChangeRequestsChartOptions(buildIncidentChartOption([], [], 'Change Requests'));
            SetServiceRequestsChartOptions(buildIncidentChartOption([], [], 'Service Requests'));
            SetProblemTicketsChartOptions(buildIncidentChartOption([], [], 'Problem Tickets'));
        } finally {
            SetIncidentsChartLoader(false);
        }
    };

    const buildIncidentChartOption = (
        incidentsData: any[],
        avgData: any[],
        chartType: string
    ): Record<string, any> => {
        const incidents = incidentsData.map((item: any) => item.incidentCount);
        const avgValue = avgData[0]?.avg ?? 0;
        const avgPreviousYearIncidents = incidentsData.map(() => avgValue);
        const dates = incidentsData.map((item: any) => {
            const date = getMonthLabel(item.yearMonth);
            return date;
        });

        const allValues = [...incidents, ...avgPreviousYearIncidents];
        const maxVal = Math.max(...allValues);
        const minVal = Math.min(...allValues);
        const yMax = Math.ceil(maxVal / 10) * 10;
        const yMin = Math.max(0, Math.floor(minVal / 10) * 10);

        return {
            backgroundColor: '#003771',
            tooltip: {
                trigger: 'axis',
                textStyle: { color: '#A7ADCB' },
                confine: true,
                backgroundColor: '#003771',
                formatter: function (params: any[]) {
                    const date = params[0]?.axisValueLabel;
                    let tooltipContent = `<div><strong>Date:</strong> ${date}</div>`;
                    params.forEach((item) => {
                        tooltipContent += `<div><strong>${item.seriesName}</strong>: ${item.value}</div>`;
                    });
                    return tooltipContent;
                }
            },
            grid: { top: '7%' },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: dates,
                axisLabel: { color: '#A7ADCB' },
                splitLine: { show: false }
            },
            yAxis: {
                type: 'value',
                min: yMin,
                max: yMax,
                axisLabel: {
                    color: '#A7ADCB',
                    formatter: (value: number) => (value >= 1000 ? `${value / 1000}k` : value)
                },
                splitLine: { show: false }
            },
            series: [
                {
                    name: chartType,
                    type: 'line',
                    data: incidents,
                    symbol: 'circle',
                    color: '#009BD8',
                    lineStyle: { width: 3 },
                    label: { show: true, position: 'top', fontSize: 12, color: '#00F0F0' },
                    areaStyle: {
                        color: {
                            type: 'linear',
                            x: 0, y: 1, x2: 0, y2: 0,
                            colorStops: [{ offset: 0, color: '#053367' }, { offset: 1, color: '#009BD8' }]
                        }
                    }
                },
                {
                    name: `${moment(filterMonth).year() - 1} Average ${chartType}`,
                    label: { show: true, position: 'top', fontSize: 12, color: '#00F0F0' },
                    type: 'line',
                    data: avgPreviousYearIncidents,
                    symbol: 'none',
                    color: '#FFC239',
                    lineStyle: { width: 2 }
                }
            ],
            legend: {
                orient: 'horizontal',
                bottom: '1%',
                left: '2%',
                textStyle: { color: '#A7ADCB' },
                selectedMode: false,
                itemWidth: 10,
                itemHeight: 10,
                icon: 'roundRect'
            }
        };
    };

    // --- MODIFICATION: Updated getDynamicTitle for single-string state ---
    const getDynamicTitle = (baseTitle: string) => {
        const timeBasedChartTitles = [
            "Incidents By Priority",
            "Automated Incidents",
            "Change Requests",
            "Service Requests",
            "Problem Tickets",
            "Knowledge Base Articles (MOM)",
            "Happiness Index Trend",
            "All Incidents" // --- CHANGE : Added "All Incidents" here
        ];

        const isTimeBasedChart = timeBasedChartTitles.includes(baseTitle);

        const isSectorSelected = sectorName && !sectorName.includes('all');

        const isBUSelected = !businessUnitName.includes('all');
        const isDeptSelected = departmentName && !departmentName.includes('all');
        const isCapSelected = capabilityName && !capabilityName.includes('all');

        const isMonthSelected = filterMonth && filterMonth !== '0M';
        const isYearFilter = filterMonth?.split('-')?.filter(df => !!df)?.length === 1
        const monthLabel = isYearFilter ? filterMonth : isMonthSelected ? getMonthLabel(filterMonth) : '';
        const titleParts = [baseTitle];

        if (isBUSelected) {
            titleParts.push(businessUnitName.join(', '));
        }
        if (isDeptSelected) {
            titleParts.push(departmentName.join(', '));
        }
        if (isCapSelected) {
            titleParts.push(capabilityName.join(', '));
        }
        if (isSectorSelected) {
            titleParts.push(sectorName.filter((s: string) => s !== 'all').join(', ').toUpperCase());
        }
        if (isMonthSelected && !isTimeBasedChart) {
            titleParts.push(monthLabel);
        }

        return titleParts.join(' - ');
    };

    const domainOptions = [
        { label: 'All Domains', value: 'all' }
    ]
    const programOptions = [
        { label: 'All Programs', value: 'all' }
    ]
    // --- MODIFICATION: Dropdown options from BRD (no changes here) ---
    const businessUnitOptions = [
        { label: 'All Business Units', value: 'all' },
        { label: 'Foods', value: 'Foods' },
        { label: 'Beverages', value: 'Beverages' },
        { label: 'Global', value: 'Global' }
    ];
    // --- MODIFICATION: Updated Department Options (Removed DEMP, Added DMO/NON DMO) ---
    const departmentOptions = [
        { label: 'All Departments', value: 'all' },
        { label: 'DEMP - DMO', value: 'DEMP - DMO' },
        { label: 'DEMP - NON DMO', value: 'DEMP - NON DMO' },
        { label: 'D&AI Arch & Governance', value: 'Data & AI Arch & Governance' },
        { label: 'BI & Reporting &Automation', value: 'BI & Reporting & Automation' },
        { label: 'Data Science & Advanced Analytics', value: 'Data Science & Adv. Analytics' },
        { label: 'D&AI Product Management', value: 'Data & AI Product Management' },
        { label: 'AI Solutions & Platforms', value: 'AI Solutions & Platforms' }
    ];
    const capabilityOptions = [
        { label: 'All Capabilities', value: 'all' },
        { label: 'AIOps', value: 'AI Ops' },
        { label: 'MLOps', value: 'ML Ops' },
        { label: 'BIOps', value: 'BI Ops' },
        { label: 'DataOps', value: 'Pipeline' },
        { label: 'IntegrationOps', value: 'Integration Ops' },
        { label: 'PlatformOps', value: 'Platform' },
        { label: 'Global', value: 'Global' }
    ];

    const handleExportToExcel = () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            // Read user's saved column preference (order + visibility) from localStorage.
            // 'Summary_user_col_pref' matches the tableType="Summary" passed to CustomizedDataGrid.
            const storedPref = localStorage.getItem('Summary_user_col_pref');
            const storedColumnNames: string[] | null = storedPref ? JSON.parse(storedPref) : null;
            const preferredColumnNames: string[] = storedColumnNames?.length
                ? storedColumnNames
                : defaultColumnNames;

            // Build a name→key lookup from all available columns
            const allColumns = getColumns();
            const nameToKey: Record<string, string> = {};
            allColumns.forEach((col: any) => { nameToKey[col.name] = col.key; });

            // Skip the total-row footer during export
            const flatRows = tableData.filter((row: any) => row.id !== 'total-row');

            // Build export rows in the user-preferred column order
            const exportData = flatRows.map((row: any) => {
                const exportRow: Record<string, any> = {};
                preferredColumnNames.forEach((colName: string) => {
                    const key = nameToKey[colName];
                    if (key !== undefined) {
                        const val = row[key];
                        exportRow[colName] = val ?? '';
                    }
                });
                return exportRow;
            });

            // Build CSV in user-preferred column order
            const escapeCsv = (v: any) => `"${String(v ?? '').replaceAll('"', '""')}"`;
            const header = preferredColumnNames.filter(n => nameToKey[n] !== undefined).join(',');
            const rows = exportData.map((r: any) =>
                preferredColumnNames
                    .filter((n: string) => nameToKey[n] !== undefined)
                    .map((n: string) => escapeCsv(r[n]))
                    .join(',')
            );
            const csv = [header, ...rows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Summary_${filterMonth}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <PageWrapper>
            <PageHeader title="Summary" icon={DashboardIcon}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontSize='16px' fontWeight={600} color={theme.palette.textLight.dark}>Metrics by Open Date</Typography>
                    <Switch
                        checked={isClosed}
                        onChange={handleOpenClosedFilterChange}
                        size="small"
                        sx={{
                            width: 44, height: 22, padding: 0,
                            '& .MuiSwitch-switchBase': {
                                '&.Mui-checked': {
                                    transform: 'translateX(22px)',
                                    '& + .MuiSwitch-track': { backgroundColor: '#71758F', opacity: 1, },
                                    '& .MuiSwitch-thumb': { backgroundColor: 'white.main', },
                                },
                            },
                            '& .MuiSwitch-thumb': { width: 14, height: 14, backgroundColor: 'backgroundLight.dark', boxShadow: 'none', },
                            '& .MuiSwitch-track': { borderRadius: 14, backgroundColor: '#71758F', opacity: 1, },
                        }}
                    />
                    <Typography fontSize='16px' fontWeight={600} color={theme.palette.textLight.dark}>Metrics by Closed Date</Typography>
                </Box>
                <YearMonthPicker
                    customMonth={customMonth}
                    filterMonth={filterMonth}
                    setCustomMonth={setCustomMonth}
                    setFilterMonth={setFilterMonth}
                    handleChange={handleDateChange}
                />
                {/* <DateFilterDropdown
                    customMonth={customMonth}
                    filterMonth={filterMonth}
                    setCustomMonth={setCustomMonth}
                    setFilterMonth={setFilterMonth}
                    handleChange={handleDateChange}
                    disableFilter={false}
                    isXOps={true}
                /> */}
            </PageHeader>
            <Grid
                width={'auto'}
                minHeight="100px"
                container
                margin={'24px'}
                px={{ xs: 1, md: 2, lg: 3 }}
                sx={{
                    justifyContent: 'center',
                    alignItems: 'stretch',
                    backgroundColor: 'backgroundLight.dark',
                    borderRadius: '8px'
                }}
            >
                {summaryMessageLoading ? <LoaderComponent /> : daiAvailabilityData.map((item, index) => {
                    const getDisplayValue = () => {
                        if (item.title === 'Assignment Groups') return assignmentGroupsCount;
                        return item.value;
                    };

                    return (
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={2}
                            lg={2}
                            xl={2}
                            key={item.title}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            flexDirection="row"
                        >
                            {index !== 0 && <Divider sx={{ height: '60%' }} color={theme.palette.blue.light} variant='inset' orientation='vertical'></Divider>}
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                flexDirection="row"
                                p={2}
                                gap={2}
                                sx={{ height: '100%', textAlign: 'center' }}
                            >
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography variant="bodyText" sx={{ color: `${theme.palette.textLight.light} !important` }}>{item.title}</Typography>
                                    {item.tooltipText && (
                                        <Tooltip title={item.tooltipText} placement="top" arrow>
                                            <InfoOutlinedIcon sx={{ fontSize: 16, color: theme.palette.textLight.light, cursor: 'pointer' }} />
                                        </Tooltip>
                                    )}
                                </Box>
                                <Typography variant="bodyValue" sx={{ color: item.valueColor || 'white' }}>{getDisplayValue()}</Typography>
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>

            {/* --- MODIFICATION: Filter Section (STICKY FIX) --- */}
            {/* FIX: Use Solid White background to match page and hide content */}
            <Grid
                container
                spacing={2}
                px={{ xs: 1, md: 2, lg: 3 }}
                sx={{
                    position: 'sticky',
                    top: '110px', // Keeps tabs visible
                    zIndex: 99,
                    py: 2, // Added vertical padding for better spacing
                    backgroundColor: '#FFFFFF', // Solid White background to hide scrolling content
                    // Add bottom border/margin to create "gap" visual
                    borderBottom: '1px solid #E0E0E0',
                    marginBottom: '16px',
                    transition: 'background-color 0.3s ease'
                }}
            >
                <Grid item xs={12}>
                    <Box sx={{
                        backgroundColor: 'backgroundLight.primary',
                        borderRadius: '6px',
                        p: 1.2,
                        border: '1px solid #334155'
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subHeader" sx={{ color: '#009BDB' }}>
                                Advance Filters
                            </Typography>
                            <Button
                                variant="text"
                                onClick={handleClearAllFilters}
                                sx={{
                                    color: '#009BDB',
                                    textTransform: 'none',
                                    fontSize: '14px',
                                    '&:hover': { backgroundColor: 'rgba(0, 155, 219, 0.1)' }
                                }}
                            >
                                Clear All
                            </Button>
                        </Box>

                        <Grid container spacing={1}>
                            {/* --- MODIFICATION: Domain (Disabled) --- */}
                            <Grid item xs={12} sm={4} md={2}>
                                <MultiSelectAutocomplete
                                    options={domainOptions}
                                    value={domainOptions?.filter(opt => domainName.includes(opt.value))}
                                    onChange={handleDomainChange}
                                    placeholder="Domain"
                                    label="Domain"
                                    disabled={true}
                                />
                            </Grid>

                            {/* --- MODIFICATION: Program (Disabled) --- */}
                            <Grid item xs={12} sm={4} md={2}>
                                <MultiSelectAutocomplete
                                    options={programOptions}
                                    value={programOptions?.filter(opt => programName.includes(opt.value))}
                                    onChange={handleProgramChange}
                                    placeholder="Program"
                                    label="Program"
                                    disabled={true}
                                />
                            </Grid>

                            {/* --- MODIFICATION: Business Unit (Single-Select + Disabled Logic) --- */}
                            <Grid item xs={12} sm={4} md={2}>
                                <MultiSelectAutocomplete
                                    options={businessUnitOptions}
                                    value={businessUnitOptions?.filter(opt => businessUnitName.includes(opt.value))}
                                    onChange={handleBusinessUnitChange}
                                    placeholder="Business Unit"
                                    label="Business Unit"
                                />
                            </Grid>

                            {/* --- MODIFICATION: Department (Single-Select + Disabled Logic) --- */}
                            <Grid item xs={12} sm={4} md={2}>
                                <MultiSelectAutocomplete
                                    options={departmentOptions}
                                    value={departmentOptions?.filter(opt => departmentName.includes(opt.value))}
                                    onChange={handleDepartmentChange}
                                    placeholder="Department"
                                    label="Department"
                                />
                            </Grid>

                            {/* --- MODIFICATION: Capability (Single-Select + Disabled Logic) --- */}
                            <Grid item xs={12} sm={4} md={2}>
                                <MultiSelectAutocomplete
                                    options={capabilityOptions}
                                    value={capabilityOptions?.filter(opt => capabilityName.includes(opt.value))}
                                    onChange={handleCapabilityChange}
                                    placeholder="Capability"
                                    label="Capability"
                                />
                            </Grid>

                            {/* --- MODIFICATION: Sector (Single-Select, No Disabled Logic) --- */}
                            <Grid item xs={12} sm={4} md={2}>
                                <MultiSelectAutocomplete
                                    options={sectorOptionsList}
                                    value={sectorOptionsList?.filter(opt => sectorName.includes(opt.value))}
                                    onChange={handleSectorChange}
                                    placeholder="Sector"
                                    label="Sector"
                                />
                            </Grid>
                        </Grid>

                        {/* --- MODIFICATION: Updated Active Filters display logic --- */}
                        {(!sectorName.includes('all') || !businessUnitName.includes('all') || !departmentName.includes('all') || !capabilityName.includes('all')) && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="bodyText" sx={{ color: '#4CAF50', fontSize: '12px' }}>
                                    Active Filters: {
                                        [
                                            !sectorName.includes('all') ? `Sector: ${sectorName}` : null,
                                            !businessUnitName.includes('all') ? `Business Unit: ${businessUnitName.join(', ')}` : null,
                                            !departmentName.includes('all') ? `Department: ${departmentName}` : null,
                                            !capabilityName.includes('all') ? `Capability: ${capabilityName}` : null
                                        ].filter(Boolean).join(' | ')
                                    }
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={2} marginTop="0.4rem" px={{ xs: 1, md: 2, lg: 3 }}>
                <Grid item xs={12} sm={12} md={12} lg={6} id="incidents-by-priority"><SegmentedBarChart title={<HighlightedText text={getDynamicTitle("Incidents By Priority")} />} isLoading={chartLoading} data={[]} options={barChartOptions} /></Grid>
                <Grid item xs={12} sm={12} md={12} lg={6}>
                    <MultiLineWidget
                        title={<HighlightedText text={getDynamicTitle("Automated Incidents")} />}
                        isLoading={incidentsChartLoader}
                        data={automatedIncidentsChartOptions}
                    >
                    </MultiLineWidget>
                </Grid>

                {/* --- CHANGE "All Incidents" is now dynamic --- */}
                <Grid item xs={12} sm={12} md={12} lg={12} id="all-incidents"><SegmentedBarChart title={<HighlightedText text={getDynamicTitle("All Incidents")} />} isLoading={allIncidentChartLoading} data={[]} options={allIncidentBarChartOptions} /></Grid>

                <Grid item xs={12} sm={12} md={12} lg={6} id="platform-availability"><CustomBarChartWidget title={<HighlightedText text={getDynamicTitle("Platform Availability")} />} isLoading={avilabilityLoader} data={availabilityData} options={avilabilityBarChartOptions} /></Grid>
                <Grid item xs={12} sm={12} md={12} lg={6}>
                    <MultiLineWidget
                        title={<HighlightedText text={getDynamicTitle("Change Requests")} />}
                        isLoading={incidentsChartLoader}
                        data={changeRequestsChartOptions}
                    >
                    </MultiLineWidget>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={6}>
                    <MultiLineWidget
                        title={<HighlightedText text={getDynamicTitle("Service Requests")} />}
                        isLoading={incidentsChartLoader}
                        data={serviceRequestsChartOptions}
                    >
                    </MultiLineWidget>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={6}>
                    <MultiLineWidget
                        title={<HighlightedText text={getDynamicTitle("Problem Tickets")} />}
                        isLoading={incidentsChartLoader}
                        data={problemTicketsChartOptions}
                    >
                    </MultiLineWidget>
                </Grid>
                {p3widgetData.map((widget, index) => (<Grid item xs={12} md={12} lg={6} key={index} id="p3-incidents"><PieInfoWidget title={<HighlightedText text={getDynamicTitle(widget.title)} />} value={widget.value} change={widget.change} changeType={widget.changeType} isLoading={p3ChartLoading} seriesData={enableSectorCut[widget.title] ? widget.title === 'P3 Incidents' ? sectorMetrics.p3SectorData : sectorMetrics.mimSectorData : widget.data} customSize="350px" showDollar={false} colorMapping={widget.title === 'P3 Incidents' ? p3IncidentsColorMapping : mimColorMapping} enableSectorCut={enableSectorCut[widget.title] || false} setEnableSectorCut={(value: boolean) => setEnableSectorCut({ ...enableSectorCut, [widget.title]: value })} /> </Grid>))}

                <Grid item xs={12} md={12} lg={6} id="happiness-index-trend">
                    <ChartWidget
                        title={<HighlightedText text={getDynamicTitle("Happiness Index Trend")} />}
                        data={happinessIndexTrendData}
                        isLoading={happyTrendLoading}
                        isFinops={true}
                        isHappySignal={true}
                        lastYearAvgData={lastYearAvgData}
                        tooltipLabel="Overall employee experience score, ranging from –100 to +100."
                        relevantYear={Number(filterMonth.split('-')?.[0])}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SegmentedBarChart
                        title={<HighlightedText text={getDynamicTitle("Knowledge Base Articles (MOM)")} />}
                        isLoading={kbArticleLoading}
                        options={knowledgeBaseOptions}
                        data={[]}
                    />
                </Grid>
                <Grid item xs={12} md={12} lg={4} id="incident-intake-channel-metrics"><PieInfoWidget title={<HighlightedText text={getDynamicTitle("Incident Intake Channel Metrics")} />} value={channelMetrics.reduce((sum, a) => sum + a.value, 0)} tooltipLabel={incidentIntakeTooltip} change={0} changeType={''} isLoading={chartLoading} seriesData={channelMetrics} customSize="230px" showDollar={false} colorMapping={intakeResolutionColorMapping} /></Grid>
                <Grid item xs={12} md={12} lg={4} id="incident-resolution-metrics"><PieInfoWidget title={<HighlightedText text={getDynamicTitle("Incident Resolution Metrics")} />} value={resolutionMetrics.reduce((sum, a) => sum + a.value, 0)} tooltipLabel={incidentResolutionTooltip} change={0} changeType={''} isLoading={chartLoading} seriesData={resolutionMetrics} customSize="230px" showDollar={false} colorMapping={intakeResolutionColorMapping} /></Grid>

                <Grid item xs={12} md={12} lg={4} id="l1-l2-l3-resolution-metrics">
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'end' }}>
                        <PieInfoWidget
                            title={<HighlightedText text={getDynamicTitle("L1/L2/L3 Resolution Metrics")} />}
                            value={l1l2l3MetricsData.value}
                            change={l1l2l3MetricsData.change}
                            changeType={l1l2l3MetricsData.changeType}
                            isLoading={chartLoading}
                            seriesData={l1l2l3MetricsData.data}
                            customSize="230px"
                            showDollar={false}
                            colorMapping={l1l2l3ColorMapping}
                            sx={{ flexGrow: 1 }}
                        />
                        <Box>
                            <CaptureExportControls
                                onScreenshot={() => { }}
                                screenCapturing={false}
                                onExport={handleExportToExcel}
                                isExporting={isExporting}
                                isVisible={true}
                                showExport={true}
                                type="snow"
                                exportLabel="Export to Excel"
                                sx={{
                                    ml: 0,
                                    '& #exclude-camera': {
                                        display: 'none',
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                </Grid>

                {loader ? (<LoaderComponent pt={10} />) : (
                    <Grid item xs={12} id="top-10-technologies">
                        <CustomizedDataGrid
                            title={<HighlightedText text={getDynamicTitle("Top Assignment Group by Volume")} />}
                            tableColumns={getColumns()}
                            tableData={tableData}
                            tableType="Summary"
                            defaultColumnNames={defaultColumnNames}
                            onSorting={handleSorting}
                            currSorting={sortingInfo}
                            page={page}
                            pageSize={pageSize}
                            paginationData={paginationData}
                            listLength={tableData.length}
                            handlepageSizeChange={handlepageSizeChange}
                            handlePageChange={handlePageChange}
                            isDateRangeFilter={false}
                            isDisabled={false}
                        />
                    </Grid>
                )}
                <Grid item xs={12} id="insights">
                    <Box marginTop={4} marginBottom={5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px' }}>
                            <Typography variant="subHeader" sx={{ color: '#009BDB', mr: { xs: "1rem", sm: '2rem' } }}>{<HighlightedText text={getDynamicTitle("Insights/Actions")} />}</Typography>
                            <Divider sx={{ flexGrow: 1, backgroundColor: '#009BDB', marginLeft: 2 }} />
                        </Box>
                        <Typography variant="bodyText" sx={{ fontStyle: 'italic', color: '#71758F' }}>You can display and update Insights/Actions if any.</Typography>
                    </Box>
                    {loader ? (<LoaderComponent />) : (
                        <Grid item xs={12} sx={{ mb: 3 }}>
                            <Insights insights={insights} setInsights={setInsights} onAdd={handleAddInsight} onUpdate={handleUpdateInsight} onDelete={handleDeleteInsight} />
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </PageWrapper>
    );
}

export default ExecutiveSummary;